import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "./DataTable";
import RegisterFaq from "./RegisterFaq";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
type DataIndex = keyof Types.FaqDto;

function FaqList() {
    const alert = useAlert();

    const [faqs, setFaqs] = useState<Array<Types.FaqDto>>();
    const [currentFaq, setcurrentFaq] = useState<Types.FaqDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteFaq, setOpenDeleteFaq] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.FaqDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.FaqDto> = [
        {
            align: "center",
            title: String.faqId,
            dataIndex: "faqId",
            key: "faqId",
            ...getColumnSearchProps("faqId"),
        },
        {
            align: "center",
            title: String.faqQuestion,
            dataIndex: "faqQuestion",
            key: "faqQuestion",
            ...getColumnSearchProps("faqQuestion"),
        },
        {
            align: "center",
            title: String.faqOrder,
            dataIndex: "faqOrder",
            key: "faqOrder",
            ...getColumnSearchProps("faqOrder"),
        },
    ];

    const requestAxiosGetFaqs = async () => {
        const response = await utilAxiosWithAuth().get(Request.FAQ_LIST_URL);
        console.log(response.data);
        return response.data;
    };

    const { loading: loadingGetFaqs, error: errorGetFaqs, data: resultGetFaqs, execute: requestGetFaqs } = useAsyncAxios(requestAxiosGetFaqs);

    useEffect(() => {
        if (resultGetFaqs === null) return;
        console.log("resultGetFaqs", resultGetFaqs);

        setFaqs(resultGetFaqs.faqs);
    }, [resultGetFaqs]);

    useEffect(() => {
        if (errorGetFaqs === null) return;

        //console.log("errorGetUsers", errorGetUsers);
        alert.setAlert(errorGetFaqs.response.data.code, "Error", errorGetFaqs.response.data.message);
    }, [errorGetFaqs]);

    const requestAxiosRemoveFaq = async (faq: Types.FaqDto) => {
        const response = await utilAxiosWithAuth().delete(Request.FAQ_URL + "/" + faq?.faqId);
        return response.data;
    };

    const {
        loading: loadingRemoveFaq,
        error: errorRemoveFaq,
        data: resultRemoveFaq,
        execute: requestRemoveFaqs,
    } = useAsyncAxios(requestAxiosRemoveFaq);

    useEffect(() => {
        if (resultRemoveFaq === null) return;
        console.log("resultRemoveFaq", resultRemoveFaq);

        alert.setAlert(AlertType.SUCCESS, "FAQ 삭제 성공", "FAQ 삭제를 완료하였습니다.");

        requestGetFaqs();
    }, [resultRemoveFaq]);

    useEffect(() => {
        if (errorRemoveFaq === null) return;

        alert.setAlert(errorRemoveFaq.response.data.code, "Error", errorRemoveFaq.response.data.message);
    }, [errorGetFaqs]);

    useEffect(() => {
        requestGetFaqs();
    }, []);

    const onRow = (value: Types.FaqDto) => {
        setEditMode("none");
        setcurrentFaq(value);
    };

    const handleDeleteFaq = () => {
        if (currentFaq !== undefined) setOpenDeleteFaq(true);
    };

    const handleDeleteFaqOk = () => {
        setOpenDeleteFaq(false);
        requestRemoveFaqs(currentFaq);
    };

    const handleDeleteFaqCancel = () => {
        setOpenDeleteFaq(false);
    };

    const onRegisterFaq = () => {
        requestGetFaqs();
        setEditMode("none");
    };

    return (
        <>
            <Row justify={"space-between"}>
                <Col span={6}>
                    <Divider orientation="left">
                        <span>FAQ</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            editMode === "add" ? setEditMode("none") : setEditMode("add");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                    <Button
                        onClick={() => {
                            editMode === "update" ? setEditMode("none") : setEditMode("update");
                        }}
                    >
                        {String.userUpdate}
                    </Button>
                    <Button onClick={() => handleDeleteFaq()} danger>
                        {String.userRemove}
                    </Button>
                </Space>
            </Row>
            <DataTable hasScroll={false} columns={columns} rowKey="faqId" dataSource={faqs} onRow={onRow} />

            {editMode === "add" && <RegisterFaq editMode={editMode} onRegisterFaq={onRegisterFaq} />}

            {editMode !== "add" && currentFaq !== undefined && <RegisterFaq editMode={editMode} faq={currentFaq} onRegisterFaq={onRegisterFaq} />}

            {openDeleteFaq && (
                <Modal open={openDeleteFaq} onOk={handleDeleteFaqOk} onCancel={handleDeleteFaqCancel}>
                    {currentFaq?.faqQuestion} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default FaqList;
